import {
  useDomains,
  useDomainsPagination,
  useSyncDomain,
  useSyncDomainsFromOpenPaye,
} from '@/hooks/useDomains';
import { useDomainsStore } from '@/store/domains.store';
import { Domain, DomainFilterStatus } from '@/types/domain.types';
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Loader2,
  MoreVertical,
  RefreshCw,
  Search,
  Server,
  Trash2,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useArchiveDomain } from '@/hooks/useOrganisations';
import { useCurrentMemberRole } from '@/hooks/useCurrentMemberRole';
import { SenderConfigDisplay } from '../components';

// Search component with debounce
const SearchBar = () => {
  const { t } = useTranslation();
  const { searchQuery, setSearchQuery } = useDomainsStore();
  const [localSearch, setLocalSearch] = useState(searchQuery);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(localSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch, setSearchQuery]);

  return (
    <div className="relative min-w-0 flex-1">
      <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-neutral-400" />
      <input
        type="text"
        placeholder={t('domains.search.placeholder', 'Search domains...')}
        value={localSearch}
        onChange={e => setLocalSearch(e.target.value)}
        className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 py-3 pr-4 pl-10 transition-colors focus:ring-2"
      />
    </div>
  );
};

// Status filter component
const StatusFilter = () => {
  const { t } = useTranslation();
  const { statusFilter, setStatusFilter } = useDomainsStore();

  return (
    <div className="relative min-w-0 flex-shrink-0">
      <select
        value={statusFilter}
        onChange={e => setStatusFilter(e.target.value as DomainFilterStatus)}
        className="focus:border-primary-500 focus:ring-primary-100 w-full appearance-none rounded-xl border border-neutral-300 bg-white px-4 py-3 pr-8 transition-colors focus:ring-2 sm:w-auto"
      >
        <option value="all">{t('domains.filter.all', 'All status')}</option>
        <option value="active">{t('domains.filter.active', 'Active')}</option>
        <option value="inactive">{t('domains.filter.inactive', 'Inactive')}</option>
        <option value="error">{t('domains.filter.error', 'Error')}</option>
        <option value="archived">{t('domains.filter.archived', 'Archivé')}</option>
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 text-neutral-400" />
    </div>
  );
};

// Pagination component
const Pagination = () => {
  const { t } = useTranslation();
  const {
    currentPage,
    totalPages,
    totalItems,
    hasNext,
    hasPrev,
    goToPage,
    nextPage,
    prevPage,
  } = useDomainsPagination();

  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-center text-sm text-neutral-600 sm:text-left">
        {t('domains.pagination.showing', 'Showing')}{' '}
        {(currentPage - 1) * 10 + 1}-{Math.min(currentPage * 10, totalItems)}{' '}
        {t('domains.pagination.of', 'of')} {totalItems}
      </p>

      <div className="flex items-center justify-center gap-2">
        <button
          onClick={prevPage}
          disabled={!hasPrev}
          className="rounded-lg border border-neutral-300 p-2 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <div className="hidden items-center gap-2 sm:flex">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
            if (page > totalPages) return null;

            return (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${page === currentPage
                    ? 'bg-primary-600 text-white'
                    : 'text-neutral-700 hover:bg-neutral-50'
                  }`}
              >
                {page}
              </button>
            );
          })}
        </div>

        {/* Mobile pagination info */}
        <div className="px-3 py-2 text-sm font-medium text-neutral-700 sm:hidden">
          {currentPage} / {totalPages}
        </div>

        <button
          onClick={nextPage}
          disabled={!hasNext}
          className="rounded-lg border border-neutral-300 p-2 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

// Action Menu Component
export const ActionMenu = ({ domain }: { domain: Domain }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: archiveDomain, isPending: isArchiving } = useArchiveDomain();

  const handleArchive = () => {
    archiveDomain({ domainUuid: domain.uuid, archive: !domain.isArchived });
    setIsOpen(false);
  };

  const handleDelete = () => {
    // TODO: Implement delete functionality
    console.log('Delete domain:', domain.uuid);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isArchiving}
        className="inline-flex items-center justify-center rounded-xl bg-neutral-50 p-2.5 text-neutral-600 transition-colors hover:bg-neutral-100 disabled:opacity-50"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute top-full right-0 z-20 mt-2 w-56 rounded-xl border border-neutral-200 bg-white shadow-lg">
            <div className="p-2">
              <button
                onClick={handleArchive}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
              >
                {domain.isArchived ? (
                  <>
                    <ArchiveRestore className="h-4 w-4 text-neutral-500" />
                    {t('domains.actions.unarchive', 'Désarchiver le domaine')}
                  </>
                ) : (
                  <>
                    <Archive className="h-4 w-4 text-neutral-500" />
                    {t('domains.actions.archive', 'Archiver le domaine')}
                  </>
                )}
              </button>
              <button
                onClick={handleDelete}
                className="text-error-700 hover:bg-error-50 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors"
              >
                <Trash2 className="text-error-500 h-4 w-4" />
                {t('domains.actions.delete', 'Supprimer le domaine')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Domain Card Component
const DomainCard = ({ domain }: { domain: Domain }) => {
  const { t } = useTranslation();
  const { mutate: syncDomain } = useSyncDomain();
  const { syncingDomainId } = useDomainsStore();
  const { isAdmin } = useCurrentMemberRole();

  const isCurrentlySyncing =
    syncingDomainId === domain.uuid || domain.status === 'syncing';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-success-100 text-success-800 border-success-200';
      case 'disconnected':
        return 'bg-neutral-100 text-neutral-800 border-neutral-200';
      case 'error':
        return 'bg-error-100 text-error-800 border-red-200';
      case 'syncing':
        return 'bg-warning-100 text-warning-800 border-warning-200';
      default:
        return 'bg-neutral-100 text-neutral-800 border-neutral-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'connected':
        return t('domains.status.connected', 'Connected');
      case 'disconnected':
        return t('domains.status.disconnected', 'Disconnected');
      case 'error':
        return t('domains.status.error', 'Error');
      case 'syncing':
        return t('domains.status.syncing', 'Syncing');
      default:
        return status;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    // <Link
    //   to={`/domains/${domain.uuid}`}
    //   className="block w-full hover:no-underline"
    // >
    <div className="group hover:border-primary-200 rounded-2xl border border-neutral-200 bg-white p-4 transition-all duration-200 hover:shadow-lg sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-start gap-3">
            <div className="bg-primary-50 group-hover:bg-primary-100 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl transition-colors">
              <Server className="text-primary-600 h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                <h3 className="group-hover:text-primary-700 truncate font-bold text-neutral-900 transition-colors">
                  {domain.name}
                </h3>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${getStatusColor(domain.status)} self-start sm:self-auto`}
                >
                  {isCurrentlySyncing && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                  {getStatusText(domain.status)}
                </span>
                {domain.isArchived && (
                  <span className="inline-flex items-center gap-1 self-start rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 sm:self-auto">
                    <Archive className="h-3 w-3" />
                    {t('domains.status.archived', 'Archivé')}
                  </span>
                )}
              </div>
              <p className="mb-4 line-clamp-2 text-sm text-neutral-600">
                {t('domains.syncedFromOpenPaye')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
            <div>
              <p className="font-medium text-neutral-500">
                {t('domains.folders.title', 'Folders')}
              </p>
              <p className="mt-1 text-lg font-bold text-neutral-900">
                {domain.foldersCount}
              </p>
            </div>
            <div>
              <p className="font-medium text-neutral-500">
                {t('domains.lastSync', 'Last Sync')}
              </p>
              <p className="mt-1 text-sm font-semibold text-neutral-900">
                {formatDate(domain.lastSyncAt)}
              </p>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <p className="font-medium text-neutral-500">
                {t('domains.syncStatus', 'Status')}
              </p>
              <p className="mt-1 text-sm font-semibold text-neutral-900">
                {domain.lastSyncStatus || '-'}
              </p>
            </div>
          </div>

          {/* Sender Configuration */}
          <div className="mt-4 border-t border-neutral-200 pt-4">
            <SenderConfigDisplay
              senderConfigurations={domain.senderConfigurations}
              onConfigure={() => {
                // TODO: Navigate to domain detail page with sender config modal open
                console.log('Configure sender for domain:', domain.uuid);
              }}
              size="sm"
            />
          </div>
        </div>

        <div className="flex flex-row items-center gap-2 lg:ml-6 lg:flex-col lg:items-end lg:gap-3">
          <Link
            to={`/domains/${domain.uuid}`}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-neutral-50 px-4 py-2.5 text-sm font-semibold text-neutral-700 transition-all duration-200 hover:bg-neutral-100 lg:flex-initial"
          >
            <FolderOpen className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t('domains.viewDetails', 'View Details')}
            </span>
            <span className="sm:hidden">{t('domains.view', 'View')}</span>
          </Link>
          <button
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              if (!isCurrentlySyncing) {
                syncDomain(domain.uuid);
              }
            }}
            disabled={isCurrentlySyncing}
            aria-label={t('domains.sync', 'Actualiser les dossiers')}
            className="bg-primary-50 text-primary-700 hover:bg-primary-100 inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 disabled:opacity-50 lg:flex-initial"
          >
            <RefreshCw
              className={`h-4 w-4 ${isCurrentlySyncing ? 'animate-spin' : ''}`}
            />
            <span className="hidden sm:inline">
              {t('domains.sync', 'Actualiser les dossiers')}
            </span>
            <span className="sm:hidden">
              {t('domains.refresh', 'Actualiser')}
            </span>
          </button>
          {isAdmin && <ActionMenu domain={domain} />}
        </div>
      </div>
    </div>
    // </Link>
  );
};

// Empty state component
const EmptyState = () => {
  const { t } = useTranslation();
  const { mutate: syncDomains, isPending } = useSyncDomainsFromOpenPaye();

  return (
    <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-8 text-center sm:p-16">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100 sm:h-16 sm:w-16">
        <FolderOpen className="h-6 w-6 text-neutral-400 sm:h-8 sm:w-8" />
      </div>
      <h3 className="mb-2 text-lg font-semibold text-neutral-900">
        {t('domains.empty.title', 'No domains found')}
      </h3>
      <p className="mx-auto mb-6 max-w-md text-sm text-neutral-600 sm:text-base">
        {t(
          'domains.empty.description',
          'Importez la liste de vos domaines et dossiers OpenPaye pour commencer'
        )}
      </p>
      <button
        onClick={() => syncDomains()}
        disabled={isPending}
        className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold text-white transition-colors disabled:opacity-50"
      >
        {isPending ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <RefreshCw className="h-5 w-5" />
        )}
        {t('domains.empty.sync', 'Importer les domaines et dossiers')}
      </button>
    </div>
  );
};

// Main Domains Page
export const DomainsPage = () => {
  const { t } = useTranslation();
  const { domains, isLoading, error } = useDomains();
  const { mutate: syncDomains, isPending: isSyncingAll } =
    useSyncDomainsFromOpenPaye();

  if (isLoading) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
            <FolderOpen className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('domains.title', 'Domains')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t('domains.subtitle', 'Manage your OpenPaye domains')}
            </p>
          </div>
        </div>

        <div className="animate-pulse space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="h-12 flex-1 rounded-xl bg-neutral-200"></div>
            <div className="h-12 w-full rounded-xl bg-neutral-200 sm:w-48"></div>
          </div>
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-2xl bg-neutral-200 sm:h-40"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
            <FolderOpen className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('domains.title', 'Domains')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t('domains.subtitle', 'Manage your OpenPaye domains')}
            </p>
          </div>
        </div>

        <div className="border-red-200 bg-error-50 rounded-2xl border p-8 text-center sm:p-12">
          <div className="bg-error-100 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full sm:h-16 sm:w-16">
            <Users className="text-error-600 h-6 w-6 sm:h-8 sm:w-8" />
          </div>
          <h3 className="text-error-900 mb-2 text-lg font-semibold">
            {t('domains.error.title', 'Failed to load domains')}
          </h3>
          <p className="text-error-700 mx-auto max-w-md text-sm sm:text-base">
            {t('domains.error.description', 'Please try again later')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
            <FolderOpen className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('domains.title', 'Domains')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t('domains.subtitle', 'Manage your OpenPaye domains')}
            </p>
          </div>
        </div>

        {domains.length > 0 && (
          <button
            onClick={() => syncDomains()}
            disabled={isSyncingAll}
            className="bg-primary-600 hover:bg-primary-700 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold text-white transition-colors disabled:opacity-50 sm:w-auto"
          >
            {isSyncingAll ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <RefreshCw className="h-5 w-5" />
            )}
            {t('domains.syncAll', 'Actualiser tous les dossiers')}
          </button>
        )}
      </div>

      {domains.length === 0 ? (
        <>
          {/* Search and Filter */}
          <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
            <SearchBar />
            <StatusFilter />
          </div>

          <EmptyState />
        </>
      ) : (
        <>
          {/* Search and Filter */}
          <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
            <SearchBar />
            <StatusFilter />
          </div>

          {/* Domains List */}
          <div className="space-y-4">
            {domains.map((domain: Domain) => (
              <DomainCard key={domain.uuid} domain={domain} />
            ))}
          </div>

          {/* Pagination */}
          <Pagination />
        </>
      )}
    </div>
  );
};
